import { useCallback, useMemo, useState } from 'react';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { useProjectsQuery } from '@/features/projects/hooks/projects';
import type { ProjectDto } from '@flowqa/shared';

type ProjectFilterOption = { value: string; label: string };

type UseScopedProjectFilterParams = {
  enabled?: boolean;
};

type UseScopedProjectFilterResult = {
  selectedSidebarProjectId: string | null;
  effectiveProjectId: string | null;
  showProjectFilter: boolean;
  selectedProjects: string[];
  handleProjectsChange: (nextProjects: string[]) => void;
  projectOptions: ProjectDto[];
  projectFilterOptions: ProjectFilterOption[];
};

export const useScopedProjectFilter = ({
  enabled = true,
}: UseScopedProjectFilterParams = {}): UseScopedProjectFilterResult => {
  const { selectedProjectId } = useProjectFilter();
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const showProjectFilter = selectedProjectId === null;
  const effectiveProjectId =
    selectedProjectId ?? (selectedProjects.length === 1 ? (selectedProjects[0] ?? null) : null);
  const projectsQuery = useProjectsQuery({ enabled });
  const projectOptions = projectsQuery.data?.items ?? [];
  const projectFilterOptions = useMemo(
    () =>
      projectOptions.map((project) => ({
        value: project.id,
        label: project.name,
      })),
    [projectOptions],
  );
  const handleProjectsChange = useCallback((nextProjects: string[]) => {
    setSelectedProjects(nextProjects);
  }, []);

  return {
    selectedSidebarProjectId: selectedProjectId,
    effectiveProjectId,
    showProjectFilter,
    selectedProjects,
    handleProjectsChange,
    projectOptions,
    projectFilterOptions,
  };
};
